/**
 * Système de Remises Gamifié - Boutique du Plaisir
 * Version DYNAMIQUE avec paramètres personnalisables depuis l'admin Shopify
 * NOUVELLE FONCTIONNALITÉ : Configuration entièrement personnalisable
 */

class DiscountGamification {
  constructor() {
    // Configuration par défaut (fallback si les settings ne sont pas disponibles)
    this.defaultLevels = [
      { threshold: 100, discount: 5, name: 'Bronze', emoji: '🥉', color: 'bronze' },
      { threshold: 200, discount: 10, name: 'Argent', emoji: '🥈', color: 'silver' },
      { threshold: 300, discount: 15, name: 'Or', emoji: '🥇', color: 'gold' },
      { threshold: 400, discount: 20, name: 'Platine', emoji: '💎', color: 'platinum' },
      { threshold: 500, discount: 25, name: 'Diamant', emoji: '💎✨', color: 'diamond' }
    ];
    
    // Configuration dynamique depuis les settings Shopify
    this.levels = this.loadDynamicSettings();
    this.settings = this.loadVisualSettings();
    
    this.cartTotal = 0;
    this.currentLevel = null;
    this.nextLevel = null;
    this.container = null;
    this.initialized = false;
    
    // Initialisation immédiate
    this.init();
  }
  
  /**
   * Charge les paramètres dynamiques depuis les settings Shopify
   */
  loadDynamicSettings() {
    try {
      // Récupération des settings depuis window.themeSettings ou directement depuis Shopify
      const settings = window.themeSettings || window.Shopify?.theme?.settings || {};
      
      // Vérifier si le système est activé
      if (!settings.rewards_enabled) {
        return [];
      }
      
      const levels = [];
      
      // Niveau Bronze
      if (settings.bronze_threshold && settings.bronze_discount) {
        levels.push({
          threshold: parseInt(settings.bronze_threshold) || 100,
          discount: parseInt(settings.bronze_discount) || 5,
          name: settings.bronze_name || 'Bronze',
          emoji: settings.bronze_emoji || '🥉',
          color: 'bronze'
        });
      }
      
      // Niveau Argent
      if (settings.silver_threshold && settings.silver_discount) {
        levels.push({
          threshold: parseInt(settings.silver_threshold) || 200,
          discount: parseInt(settings.silver_discount) || 10,
          name: settings.silver_name || 'Argent',
          emoji: settings.silver_emoji || '🥈',
          color: 'silver'
        });
      }
      
      // Niveau Or
      if (settings.gold_threshold && settings.gold_discount) {
        levels.push({
          threshold: parseInt(settings.gold_threshold) || 300,
          discount: parseInt(settings.gold_discount) || 15,
          name: settings.gold_name || 'Or',
          emoji: settings.gold_emoji || '🥇',
          color: 'gold'
        });
      }
      
      // Niveau Platine
      if (settings.platinum_threshold && settings.platinum_discount) {
        levels.push({
          threshold: parseInt(settings.platinum_threshold) || 400,
          discount: parseInt(settings.platinum_discount) || 20,
          name: settings.platinum_name || 'Platine',
          emoji: settings.platinum_emoji || '💎',
          color: 'platinum'
        });
      }
      
      // Niveau Diamant
      if (settings.diamond_threshold && settings.diamond_discount) {
        levels.push({
          threshold: parseInt(settings.diamond_threshold) || 500,
          discount: parseInt(settings.diamond_discount) || 25,
          name: settings.diamond_name || 'Diamant',
          emoji: settings.diamond_emoji || '💎✨',
          color: 'diamond'
        });
      }
      
      // Trier les niveaux par seuil croissant
      levels.sort((a, b) => a.threshold - b.threshold);
      
      return levels.length > 0 ? levels : this.defaultLevels;
    } catch (error) {
      console.warn('Erreur lors du chargement des paramètres de récompenses:', error);
      return this.defaultLevels;
    }
  }
  
  /**
   * Charge les paramètres visuels depuis les settings Shopify
   */
  loadVisualSettings() {
    try {
      const settings = window.themeSettings || window.Shopify?.theme?.settings || {};
      
      return {
        primaryColor: settings.rewards_primary_color || '#E91E63',
        secondaryColor: settings.rewards_secondary_color || '#FF6B9D',
        title: settings.rewards_title || 'Débloquez vos récompenses !',
        subtitle: settings.rewards_subtitle || 'Plus vous achetez, plus vous économisez',
        showOnCart: settings.show_rewards_on_cart !== false,
        showOnDrawer: settings.show_rewards_on_drawer !== false,
        showOnProduct: settings.show_rewards_on_product !== false,
        showOnCollection: settings.show_rewards_on_collection !== false
      };
    } catch (error) {
      console.warn('Erreur lors du chargement des paramètres visuels:', error);
      return {
        primaryColor: '#E91E63',
        secondaryColor: '#FF6B9D',
        title: 'Débloquez vos récompenses !',
        subtitle: 'Plus vous achetez, plus vous économisez',
        showOnCart: true,
        showOnDrawer: true,
        showOnProduct: true,
        showOnCollection: true
      };
    }
  }
  
  /**
   * Applique les couleurs personnalisées au CSS
   */
  applyCustomColors() {
    if (!this.settings.primaryColor || !this.settings.secondaryColor) return;
    
    // Créer ou mettre à jour les variables CSS personnalisées
    const style = document.getElementById('rewards-custom-colors') || document.createElement('style');
    style.id = 'rewards-custom-colors';
    
    style.textContent = `
      :root {
        --rewards-primary-color: ${this.settings.primaryColor};
        --rewards-secondary-color: ${this.settings.secondaryColor};
      }
      
      .discount-gamification {
        border-color: ${this.settings.primaryColor}33;
      }
      
      .discount-gamification::before {
        background: linear-gradient(90deg, ${this.settings.primaryColor}, ${this.settings.secondaryColor}, ${this.settings.primaryColor});
      }
      
      .discount-title {
        color: ${this.settings.primaryColor};
      }
      
      .discount-progress-fill {
        background: linear-gradient(90deg, ${this.settings.primaryColor} 0%, ${this.settings.secondaryColor} 50%, ${this.settings.primaryColor} 100%);
      }
      
      .discount-current-info {
        background: linear-gradient(135deg, ${this.settings.primaryColor}, ${this.settings.secondaryColor});
      }
      
      .discount-next-level {
        background: ${this.settings.primaryColor}1A;
        border-color: ${this.settings.primaryColor}33;
        color: ${this.settings.primaryColor};
      }
      
      .discount-badge.active {
        border-color: ${this.settings.primaryColor};
        box-shadow: 0 0 20px ${this.settings.primaryColor}80;
      }
      
      .discount-mini__progress-fill {
        background: linear-gradient(90deg, ${this.settings.primaryColor} 0%, ${this.settings.secondaryColor} 100%);
      }
      
      .discount-mini__badge.active {
        background: ${this.settings.primaryColor};
      }
      
      .discount-compact__active {
        background: linear-gradient(90deg, ${this.settings.primaryColor} 0%, ${this.settings.secondaryColor} 100%);
      }
      
      .discount-collection__cta {
        background: linear-gradient(90deg, ${this.settings.primaryColor} 0%, ${this.settings.secondaryColor} 100%);
      }
    `;
    
    if (!document.getElementById('rewards-custom-colors')) {
      document.head.appendChild(style);
    }
  }
  
  init() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    console.log('Discount gamification: Setting up with dynamic settings...');
    
    // Appliquer les couleurs personnalisées
    this.applyCustomColors();
    
    // Vérifier si le système est activé
    if (this.levels.length === 0) {
      console.log('Système de récompenses désactivé');
      return;
    }
    
    // Créer le widget sur les pages appropriées
    this.createDiscountWidget();
    this.updateCartTotal();
    this.bindEvents();
    this.initialized = true;
    
    console.log('Discount gamification: Setup complete with', this.levels.length, 'levels');
  }
  
  isCartPage() {
    if (!this.settings.showOnCart) return false;
    
    const isCartPage = window.location.pathname === '/cart' || 
                      window.location.pathname.endsWith('/cart') ||
                      document.querySelector('.section--main-cart') !== null;
    
    return isCartPage;
  }
  
  isCollectionPage() {
    if (!this.settings.showOnCollection) return false;
    
    return window.location.pathname.includes('/collections/') ||
           document.querySelector('.section--main-collection') !== null;
  }
  
  isProductPage() {
    if (!this.settings.showOnProduct) return false;
    
    return window.location.pathname.includes('/products/') ||
           document.querySelector('.section--main-product') !== null;
  }
  
  createDiscountWidget() {
    // Différents types d'affichage selon la page
    if (this.isCartPage()) {
      this.createCartWidget();
    } else if (this.isCollectionPage()) {
      this.createCollectionWidget();
    } else if (this.isProductPage()) {
      this.createProductWidget();
    }
    
    // Toujours créer le widget pour le panier latéral si activé
    if (this.settings.showOnDrawer) {
      this.createDrawerWidget();
    }
  }
  
  createCartWidget() {
    const targetContainer = document.querySelector('#discount-gamification-container') ||
                           document.querySelector('.order-summary') ||
                           document.querySelector('form[action*="cart"]') ||
                           document.querySelector('.cart-items');
    
    if (!targetContainer) return;
    
    this.container = document.createElement('div');
    this.container.className = 'discount-gamification discount-gamification--cart';
    this.container.innerHTML = this.generateHTML('full');
    
    if (targetContainer.id === 'discount-gamification-container') {
      targetContainer.appendChild(this.container);
    } else {
      targetContainer.parentNode.insertBefore(this.container, targetContainer);
    }
  }
  
  createDrawerWidget() {
    // Observer pour détecter l'ouverture du panier latéral
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const drawer = mutation.target;
          if (drawer.hasAttribute('open') && !drawer.querySelector('.discount-gamification')) {
            this.insertDrawerWidget(drawer);
          }
        }
      });
    });
    
    // Observer le cart-drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      observer.observe(cartDrawer, { attributes: true });
      
      // Si déjà ouvert, insérer immédiatement
      if (cartDrawer.hasAttribute('open')) {
        this.insertDrawerWidget(cartDrawer);
      }
    }
  }
  
  insertDrawerWidget(drawer) {
    const targetContainer = drawer.querySelector('#discount-gamification-drawer') ||
                           drawer.querySelector('.cart-drawer__items') ||
                           drawer.querySelector('.cart-drawer__footer');
    
    if (!targetContainer || targetContainer.querySelector('.discount-gamification')) return;
    
    const widget = document.createElement('div');
    widget.className = 'discount-gamification discount-gamification--drawer';
    
    // Déterminer le mode d'affichage basé sur les données du conteneur
    const displayMode = targetContainer.dataset.displayMode || 'compact';
    widget.innerHTML = this.generateHTML(displayMode);
    
    if (targetContainer.id === 'discount-gamification-drawer') {
      targetContainer.innerHTML = '';
      targetContainer.appendChild(widget);
    } else if (targetContainer.classList.contains('cart-drawer__items')) {
      targetContainer.parentNode.insertBefore(widget, targetContainer.nextSibling);
    } else {
      targetContainer.parentNode.insertBefore(widget, targetContainer);
    }
  }
  
  createCollectionWidget() {
    // Insérer à la place du 3ème produit dans la grille
    const productGrid = document.querySelector('.product-list') || 
                       document.querySelector('.collection-grid') ||
                       document.querySelector('[data-products]');
    
    if (!productGrid) return;
    
    const products = productGrid.querySelectorAll('.product-card, .product-item');
    
    if (products.length >= 3) {
      const widget = document.createElement('div');
      widget.className = 'discount-gamification discount-gamification--collection product-card';
      widget.innerHTML = this.generateHTML('collection');
      
      // Insérer après le 2ème produit (à la place du 3ème)
      products[2].parentNode.insertBefore(widget, products[2]);
    }
  }
  
  createProductWidget() {
    // Widget compact sur la fiche produit
    const targetContainer = document.querySelector('.product-info') ||
                           document.querySelector('.product-form') ||
                           document.querySelector('.product-details');
    
    if (!targetContainer) return;
    
    const widget = document.createElement('div');
    widget.className = 'discount-gamification discount-gamification--product';
    widget.innerHTML = this.generateHTML('mini');
    
    // Insérer après le formulaire d'ajout au panier
    const addToCartForm = targetContainer.querySelector('form[action*="cart"]');
    if (addToCartForm) {
      addToCartForm.parentNode.insertBefore(widget, addToCartForm.nextSibling);
    } else {
      targetContainer.appendChild(widget);
    }
  }
  
  generateHTML(type = 'full') {
    const currentLevel = this.getCurrentLevel();
    const nextLevel = this.getNextLevel();
    const progress = this.getProgress();
    
    if (type === 'mini') {
      return `
        <div class="discount-mini">
          <div class="discount-mini__header">
            <span class="discount-mini__icon">🎁</span>
            <span class="discount-mini__text">Récompenses disponibles</span>
          </div>
          <div class="discount-mini__progress">
            <div class="discount-mini__progress-bar">
              <div class="discount-mini__progress-fill" style="width: ${progress}%"></div>
            </div>
            <div class="discount-mini__progress-text">
              ${this.cartTotal.toFixed(0)}€ / ${this.levels[this.levels.length - 1].threshold}€
            </div>
          </div>
          <div class="discount-mini__levels">
            ${this.levels.map(level => `
              <span class="discount-mini__badge ${level.color} ${this.cartTotal >= level.threshold ? 'active' : ''}" 
                    title="${level.name} - ${level.discount}% à partir de ${level.threshold}€">
                ${level.discount}%
              </span>
            `).join('')}
          </div>
          ${nextLevel ? `
            <div class="discount-mini__next">
              +${(nextLevel.threshold - this.cartTotal).toFixed(0)}€ pour ${nextLevel.discount}% de réduction
            </div>
          ` : ''}
        </div>
      `;
    }
    
    if (type === 'collection') {
      return `
        <div class="discount-collection">
          <div class="discount-collection__content">
            <div class="discount-collection__header">
              <div class="discount-collection__icon">🎁</div>
              <h3>${this.settings.title}</h3>
              <p>${this.settings.subtitle}</p>
            </div>
            <div class="discount-collection__levels">
              ${this.levels.map((level, index) => `
                <div class="discount-collection__level ${level.color}">
                  <span class="discount-collection__badge">${level.emoji}</span>
                  <div class="discount-collection__info">
                    <span class="discount-collection__name">${level.name}</span>
                    <span class="discount-collection__discount">${level.discount}%</span>
                  </div>
                  <span class="discount-collection__threshold">${level.threshold}€+</span>
                </div>
              `).join('')}
            </div>
            <div class="discount-collection__cta">
              <span>Commencez vos achats pour débloquer !</span>
            </div>
          </div>
        </div>
      `;
    }
    
    if (type === 'compact') {
      return `
        <div class="discount-title">
          <span class="discount-icon">🎁</span>
          <h3>Récompenses disponibles</h3>
        </div>
        
        <div class="discount-progress">
          <div class="discount-progress-bar">
            <div class="discount-progress-fill" style="width: ${progress}%"></div>
          </div>
        </div>
        
        <div class="discount-levels">
          ${this.levels.map(level => `
            <div class="discount-badge ${level.color} ${this.cartTotal >= level.threshold ? 'active' : ''}" 
                 title="${level.name} - ${level.discount}% de réduction à partir de ${level.threshold}€">
              <span class="discount-badge-percent">${level.discount}%</span>
            </div>
          `).join('')}
        </div>
        
        <div class="discount-status">
          ${this.generateStatusMessage()}
        </div>
      `;
    }
    
    if (type === 'medium') {
      return `
        <div class="discount-title">
          <span class="discount-icon">🎮</span>
          <h3>${this.settings.title}</h3>
        </div>
        
        <div class="discount-progress">
          <div class="discount-progress-bar">
            <div class="discount-progress-fill" style="width: ${progress}%"></div>
          </div>
        </div>
        
        <div class="discount-levels">
          ${this.levels.map(level => `
            <div class="discount-badge ${level.color} ${this.cartTotal >= level.threshold ? 'active' : ''}" 
                 title="${level.name} - ${level.discount}% de réduction">
              <span class="discount-badge-emoji">${level.emoji}</span>
              <span class="discount-badge-percent">${level.discount}%</span>
            </div>
          `).join('')}
        </div>
        
        <div class="discount-status">
          ${this.generateStatusMessage()}
        </div>
        
        <div class="discount-next">
          ${this.generateNextLevelMessage()}
        </div>
      `;
    }
    
    // Version complète pour la page panier
    return `
      <div class="discount-title">
        <span class="discount-icon">🎮</span>
        <h3>${this.settings.title}</h3>
      </div>
      
      <div class="discount-progress">
        <div class="discount-progress-bar">
          <div class="discount-progress-fill" style="width: ${progress}%"></div>
        </div>
      </div>
      
      <div class="discount-levels">
        ${this.levels.map(level => `
          <div class="discount-badge ${level.color} ${this.cartTotal >= level.threshold ? 'active' : ''}" 
               title="${level.name} - ${level.discount}% de réduction">
            <span class="discount-badge-emoji">${level.emoji}</span>
            <span class="discount-badge-name">${level.name}</span>
            <span class="discount-badge-percent">${level.discount}%</span>
          </div>
        `).join('')}
      </div>
      
      <div class="discount-status">
        ${this.generateStatusMessage()}
      </div>
      
      <div class="discount-next">
        ${this.generateNextLevelMessage()}
      </div>
    `;
  }
  
  generateStatusMessage() {
    const currentLevel = this.getCurrentLevel();
    
    if (currentLevel) {
      const savings = (this.cartTotal * currentLevel.discount / 100).toFixed(2);
      return `
        <div class="discount-status-active">
          <strong>${currentLevel.discount}% de réduction !</strong><br>
          Niveau ${currentLevel.name} débloqué ! Vous économisez ${savings}€
        </div>
      `;
    } else {
      return `
        <div class="discount-status-inactive">
          <strong>Aucune remise active</strong><br>
          Ajoutez des produits pour débloquer vos récompenses !
        </div>
      `;
    }
  }
  
  generateNextLevelMessage() {
    const nextLevel = this.getNextLevel();
    
    if (nextLevel) {
      const remaining = (nextLevel.threshold - this.cartTotal).toFixed(2);
      return `
        <div class="discount-next-level">
          Plus que ${remaining}€ pour débloquer <strong>${nextLevel.name} ${nextLevel.discount}%</strong> !
        </div>
      `;
    } else {
      return `
        <div class="discount-max-level">
          🎉 Niveau maximum atteint ! Profitez de votre réduction !
        </div>
      `;
    }
  }
  
  getCurrentLevel() {
    return this.levels.slice().reverse().find(level => this.cartTotal >= level.threshold) || null;
  }
  
  getNextLevel() {
    return this.levels.find(level => this.cartTotal < level.threshold) || null;
  }
  
  getProgress() {
    if (this.cartTotal === 0 || this.levels.length === 0) return 0;
    
    const maxThreshold = this.levels[this.levels.length - 1].threshold;
    return Math.min((this.cartTotal / maxThreshold) * 100, 100);
  }
  
  updateCartTotal() {
    // Récupérer le total depuis Shopify
    if (window.shopifyCartData && window.shopifyCartData.originalTotalPrice) {
  this.cartTotal = window.shopifyCartData.originalTotalPrice / 100;
} else if (window.shopifyCartData && window.shopifyCartData.totalPrice) {
  this.cartTotal = window.shopifyCartData.totalPrice / 100;
    } else if (window.cart && window.cart.total_price) {
      this.cartTotal = window.cart.total_price / 100;
    } else {
      // Fallback: chercher dans le DOM
      const totalElement = document.querySelector('[data-cart-total]') || 
                          document.querySelector('.cart-total') ||
                          document.querySelector('.order-summary__total');
      
      if (totalElement) {
        const totalText = totalElement.textContent || totalElement.innerText;
        const totalMatch = totalText.match(/(\d+(?:,\d+)?(?:\.\d+)?)/);
        if (totalMatch) {
          this.cartTotal = parseFloat(totalMatch[1].replace(',', '.'));
        }
      }
    }
    
    this.updateDisplay();
  }
  
  updateDisplay() {
    const widgets = document.querySelectorAll('.discount-gamification');
    widgets.forEach(widget => {
      // Barres de progression principales
      const progressFill = widget.querySelector('.discount-progress-fill');
      if (progressFill) {
        progressFill.style.width = this.getProgress() + '%';
      }
      
      // Barres de progression mini (fiches produits)
      const miniProgressFill = widget.querySelector('.discount-mini__progress-fill');
      if (miniProgressFill) {
        miniProgressFill.style.width = this.getProgress() + '%';
      }
      
      // Texte de progression mini
      const miniProgressText = widget.querySelector('.discount-mini__progress-text');
      if (miniProgressText && this.levels.length > 0) {
        miniProgressText.textContent = `${this.cartTotal.toFixed(0)}€ / ${this.levels[this.levels.length - 1].threshold}€`;
      }
      
      // Badges
      const badges = widget.querySelectorAll('.discount-badge, .discount-mini__badge');
      badges.forEach((badge, index) => {
        const level = this.levels[index];
        if (level && this.cartTotal >= level.threshold) {
          badge.classList.add('active');
        } else if (level) {
          badge.classList.remove('active');
        }
      });
      
      // Messages de statut
      const statusDiv = widget.querySelector('.discount-status');
      if (statusDiv) {
        statusDiv.innerHTML = this.generateStatusMessage();
      }
      
      const nextDiv = widget.querySelector('.discount-next');
      if (nextDiv) {
        nextDiv.innerHTML = this.generateNextLevelMessage();
      }
      
      // Messages mini next
      const miniNext = widget.querySelector('.discount-mini__next');
      if (miniNext) {
        const nextLevel = this.getNextLevel();
        if (nextLevel) {
          miniNext.textContent = `+${(nextLevel.threshold - this.cartTotal).toFixed(0)}€ pour ${nextLevel.discount}% de réduction`;
        } else {
          miniNext.style.display = 'none';
        }
      }
    });
  }
  
  bindEvents() {
    // Écouter les changements du panier
    document.addEventListener('cart:updated', () => {
      this.updateCartTotal();
    });
    
    // Écouter les changements via Shopify AJAX API
    if (window.fetch) {
      const originalFetch = window.fetch;
      window.fetch = function(...args) {
        return originalFetch.apply(this, args).then(response => {
          if (args[0] && args[0].includes('/cart')) {
            setTimeout(() => {
              if (window.discountGamification) {
                window.discountGamification.updateCartTotal();
              }
            }, 100);
          }
          return response;
        });
      };
    }
    
    // Observer les changements dans le DOM du panier
    const cartObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          const target = mutation.target;
          if (target.classList && (
            target.classList.contains('cart-items') ||
            target.classList.contains('cart-drawer__items') ||
            target.querySelector && target.querySelector('.cart-total, [data-cart-total]')
          )) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        setTimeout(() => this.updateCartTotal(), 200);
      }
    });
    
    // Observer le panier principal
    const cartContainer = document.querySelector('.cart-items, .cart-drawer__items, .main-cart');
    if (cartContainer) {
      cartObserver.observe(cartContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    
    // Mise à jour périodique de sécurité
    setInterval(() => {
      this.updateCartTotal();
    }, 5000);
  }
  
  /**
   * Méthode publique pour recharger la configuration
   * Utile si les settings changent dynamiquement
   */
  reloadSettings() {
    this.levels = this.loadDynamicSettings();
    this.settings = this.loadVisualSettings();
    this.applyCustomColors();
    this.updateDisplay();
    console.log('Configuration du système de récompenses rechargée');
  }
}

// Initialisation automatique
if (typeof window !== 'undefined') {
  window.discountGamification = new DiscountGamification();
  
  // Exposer la méthode de rechargement pour les développeurs
  window.reloadRewardsSettings = () => {
    if (window.discountGamification) {
      window.discountGamification.reloadSettings();
    }
  };
}

// Export pour les modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DiscountGamification;
}

